import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, MessageSquare, Send } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, speakableSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand, Crumbs, FaqList } from "../components/Shared";

const contactFaqs = [
  { q: "Where is Boston Iron Grit located?", a: "We're at 1230 VFW Parkway, West Roxbury, MA 02132, with the entrance and free on-site parking on Sambuca Way Alley — minutes from Dedham, Roslindale, Brookline and Newton." },
  { q: "What's the fastest way to reach a coach?", a: "Text us at 617-637-8900 — that's the fastest way to get an answer. You can also call, email info@crossfitbostonirongrit.com, or use the contact form and we'll reply within one business day." },
  { q: "What are your hours?", a: "Monday through Friday 5:15 AM to 7:30 PM, Saturday 6:30 AM to 12:00 PM, and Sunday 8:00 AM to 12:00 PM." },
  { q: "Can I just stop by to see the gym?", a: "Yes — stop by during staffed hours and a coach will show you around, or book a free goal review so we can give you our full attention." },
];
import { SITE, IMG } from "../data/mock";
import { useToast } from "../hooks/use-toast";
import { suggestEmail } from "../lib/emailSuggest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          form_name: "contact",
          program_interest: "General Inquiry",
          page_location: window.location.href,
          traffic_source: document.referrer ? "referral" : "direct",
        });
      }
      setForm({ name: "", email: "", phone: "", message: "" });
      toast({ title: "Message sent", description: "Thanks for reaching out — we'll get back to you within one business day." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "We couldn't send your message. Please try again or call us at " + SITE.phone + "." });
    } finally {
      setSending(false);
    }
  };

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors";

  return (
    <>
      <Seo
        title="Contact Boston Iron Grit | Gym in West Roxbury, MA"
        description="Contact Boston Iron Grit at 1230 VFW Parkway, West Roxbury, MA 02132. Call or text 617-637-8900, send a message, or stop by — free parking on site."
        path="/contact"
        schemas={[
          breadcrumbSchema(crumbs),
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "@id": "https://bostonirongrittraining.com/contact#contactpage",
            url: "https://bostonirongrittraining.com/contact",
            mainEntity: { "@id": "https://bostonirongrittraining.com/#business" },
          },
          speakableSchema("/contact"),
          faqSchema(contactFaqs),
        ]}
      />
      <PageHero
        label="Contact"
        title={<>Say hello.<br /><span className="text-neutral-500">We answer fast.</span></>}
        sub="Questions about memberships, drop-ins or your first visit? Call, text or send a message — a coach (not a bot) will get back to you."
        image={IMG.interior}
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      <section className="bg-[#0A0A0B] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          {/* Info */}
          <Reveal>
            <Eyebrow>Visit Us</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight mb-8">
              West Roxbury,<br />on the Parkway
            </h2>
            <div className="space-y-5">
              <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                <MapPin size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white text-sm font-semibold" data-speakable="true">{SITE.address.full}</div>
                  <div className="text-neutral-500 text-[13px] mt-1">Entrance on Sambuca Way Alley · Free on-site parking</div>
                </div>
              </a>
              <a href={SITE.phoneHref} className="flex items-start gap-4 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                <Phone size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white text-sm font-semibold" data-speakable="true">Call us: {SITE.phone}</div>
                  <div className="text-neutral-500 text-[13px] mt-1">Weekdays 5:15 AM – 7:30 PM</div>
                </div>
              </a>
              <a href={SITE.smsHref} className="flex items-start gap-4 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                <MessageSquare size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white text-sm font-semibold">Text us: {SITE.phone}</div>
                  <div className="text-neutral-500 text-[13px] mt-1">Fastest way to reach a coach</div>
                </div>
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-start gap-4 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                <Mail size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white text-sm font-semibold">{SITE.email}</div>
                  <div className="text-neutral-500 text-[13px] mt-1">For anything that needs a paper trail</div>
                </div>
              </a>
              <div className="flex items-start gap-4 p-5 bg-[#121214] border border-white/5">
                <Clock size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
                <div className="text-[13px] text-neutral-400 leading-relaxed" data-speakable="true">
                  {SITE.hours.map((h) => (
                    <div key={h.days}><span className="text-neutral-500">{h.days}:</span> <span className="text-neutral-200">{h.time}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <Eyebrow>Send a Message</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight mb-8">
              Drop us a line
            </h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label htmlFor="c-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Name *</label>
                <input id="c-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="c-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Email *</label>
                  <input id="c-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                  {suggestEmail(form.email) && (
                    <button type="button" data-testid="contact-email-suggestion" onClick={() => setForm({ ...form, email: suggestEmail(form.email) })} className="mt-1.5 text-[12px] text-amber-400 hover:text-white transition-colors text-left">
                      Did you mean <span className="underline">{suggestEmail(form.email)}</span>?
                    </button>
                  )}
                </div>
                <div>
                  <label htmlFor="c-phone" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Phone</label>
                  <input id="c-phone" data-testid="contact-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(617) 555-0123" />
                </div>
              </div>
              <div>
                <label htmlFor="c-msg" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Message *</label>
                <textarea id="c-msg" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="How can we help?" />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-60 text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-8 py-4 transition-all duration-200 hover:translate-y-[-1px]"
              >
                {sending ? "Sending…" : "Send Message"} <Send size={14} />
              </button>
            </form>

            <div className="mt-10 h-[260px] border border-white/10 overflow-hidden">
              <iframe
                title="Map to Boston Iron Grit"
                src="https://www.google.com/maps?q=1230+VFW+Parkway,+West+Roxbury,+MA+02132&output=embed"
                className="w-full h-full"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(30%)" }}
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16" data-testid="contact-faq">
        <div className="max-w-4xl mx-auto px-6">
          <FaqList faqs={contactFaqs} title="Visiting Us — FAQs" />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
